/*
 * ====================================================================================================
 * WARNING: BACKEND-COPIED DTO FILE
 * THIS FILE IS COPIED FROM THE BACKEND.
 * DO NOT MODIFY THIS FILE MANUALLY IN FRONTEND CODE.
 * IT MAY BE DELETED, REGENERATED, OR REPLACED AT ANY TIME.
 * ====================================================================================================
 */

/**
 * Tokens clients may send in `transitModeFilters` (granular MOTIS values plus legacy API aliases).
 */
export const ALLOWED_TRANSIT_MODE_FILTER_TOKENS = [
    "BUS",
    "ODM",
    "FLEX",
    "COACH",
    "TRAM",
    "SUBWAY",
    "METRO",
    "FERRY",
    "RAIL",
    "HIGHSPEED_RAIL",
    "LONG_DISTANCE",
    "NIGHT_RAIL",
    "REGIONAL_FAST_RAIL",
    "REGIONAL_RAIL",
    "SUBURBAN",
    "FUNICULAR",
    "AREAL_LIFT",
    "CABLE_CAR",
    "OTHER",
    "TRAIN",
    "BOAT",
] as const;

/**
 * Transport modes accepted by itinerary search requests.
 * Values are API-level tokens sent by clients.
 */
export enum TransportMode {
    "FOOT" = "FOOT",
    "BIKE" = "BIKE",
    "RENTAL" = "RENTAL",
    "CARPOOL" = "CARPOOL",
    "TRANSIT" = "TRANSIT",
    "MYCAR" = "MYCAR",
}

/**
 * Rental vehicle form factors accepted by itinerary search requests.
 * Values match MOTIS `directRentalFormFactors` tokens.
 */
export enum RentalVehicleType {
    "BICYCLE" = "BICYCLE",
    "CARGO_BICYCLE" = "CARGO_BICYCLE",
    "CAR" = "CAR",
    "MOPED" = "MOPED",
    "SCOOTER_STANDING" = "SCOOTER_STANDING",
    "SCOOTER_SEATED" = "SCOOTER_SEATED",
    "OTHER" = "OTHER",
}

/**
 * Park-and-ride selection preference for custom P+R routing.
 * `ALL` expands server-side to `MAX_CAR`, `MAX_TRANSPORT`, and `BALANCED`.
 */
export enum ParkAndRidePreference {
    "ALL" = "ALL",
    "MAX_CAR" = "MAX_CAR",
    "MAX_TRANSPORT" = "MAX_TRANSPORT",
    "BALANCED" = "BALANCED",
}

/**
 * Transit sub-mode filter token accepted by itinerary search requests.
 * Values are MOTIS transit mode tokens or legacy API aliases resolved before routing.
 */
export type TransitModeFilter = typeof ALLOWED_TRANSIT_MODE_FILTER_TOKENS[number];

/**
 * Flat coordinate payload used by legacy request mappers.
 * Kept for compatibility with consumers that do not send nested locations.
 */
export interface Coordinates {
    "fromLat":   number;
    "fromLng":   number;
    "toLat":     number;
    "toLng":     number;
    "arriveBy"?: boolean;
}

/**
 * Geographic location used for itinerary search endpoints.
 */
export class ItinerarySearchLocationDTO {

    /**
     * Latitude coordinate in decimal degrees.
     * Example: 46.2044
     * Minimum: -90
     * Maximum: 90
     */
    lat!: number;

    /**
     * Longitude coordinate in decimal degrees.
     * Example: 6.1432
     * Minimum: -180
     * Maximum: 180
     */
    lon!: number;

    /**
     * ISO 3166-1 alpha-2 country code.
     * Example: CH
     * MinLength: 2
     * MaxLength: 2
     */
    country!: string;

}

/**
 * Temporal context used to run itinerary searches.
 */
export class ItinerarySearchTimeDTO {

    /**
     * Search timestamp in epoch milliseconds.
     * Example: 1713277200000
     * Minimum: 0
     */
    timestamp!: number;

    /**
     * IANA timezone identifier.
     * Example: Europe/Zurich
     * MinLength: 1
     */
    timezone?: string;

    /**
     * When true timestamp is departure-based, otherwise arrival-based.
     * Example: true
     */
    isDepartureTimestamp!: boolean;

}

/**
 * Itinerary request DTO for itinerary search across providers.
 */
export class ItinerarySearchRequestDTO {

    /**
     * Origin location for itinerary search.
     * Type: ItinerarySearchLocationDTO
     */
    from!: ItinerarySearchLocationDTO;

    /**
     * Destination location for itinerary search.
     * Type: ItinerarySearchLocationDTO
     */
    to!: ItinerarySearchLocationDTO;

    /**
     * Temporal search parameters.
     * Type: ItinerarySearchTimeDTO
     */
    time!: ItinerarySearchTimeDTO;

    /**
     * Requested transport mode list.
     * Example: [ TransportMode.TRANSIT, TransportMode.FOOT ]
     * Enum: TransportMode
     */
    modes!: TransportMode[];

    /**
     * Currency code used for price display.
     * Example: CHF
     * MinLength: 3
     * MaxLength: 3
     */
    currency?: string;

    /**
     * Cycling speed in km/h.
     * Example: 15
     * Minimum: 0
     */
    bikeSpeedKmh?: number;

    /**
     * Elevation cost tuning value used by routing engines.
     * Example: 0.5
     */
    elevationCosts?: string;

    /**
     * Enable personal bike routing.
     * Example: true
     */
    transportPersonalBike?: boolean;

    /**
     * Allow ending trip with bike after transit leg.
     * Example: false
     */
    tripEndByBike?: boolean;

    /**
     * Indicates user owns a personal bike.
     * Example: true
     */
    havePersonalBike?: boolean;

    /**
     * Rental vehicle form factors to include in search. Omit or send [] to disable rental. Send at least one value to enable rental routing.
     * Example: [ RentalVehicleType.BICYCLE, RentalVehicleType.SCOOTER_STANDING ]
     * Enum: RentalVehicleType
     */
    rentalVehicleTypes?: RentalVehicleType[];

    /**
     * MyCar itinerary profile type. Allowed values depend on MyCar provider configuration.
     * Example: FASTEST
     */
    carItineraryType?: string;

    /**
     * When true, enables kiss-and-ride (MOTIS `CAR_DROPOFF`) as a dedicated transit access option with a short pre-transit cap. Omit or false disables dropoff.
     * Example: false
     */
    carDropOff?: boolean;

    /**
     * Example: [ ParkAndRidePreference.BALANCED, ParkAndRidePreference.MAX_CAR ]
     * Enum: ParkAndRidePreference
     */
    carParkAndRide?: ParkAndRidePreference[];

    /**
     * Transit sub-modes to include in search. Omit or send [] to allow all transit modes (MOTIS `TRANSIT`). Each value is a MOTIS transit mode token or a legacy alias resolved before routing.
     * Example: [ "RAIL", "BUS", "TRAM", "OTHER" ]
     * Enum: ALLOWED_TRANSIT_MODE_FILTER_TOKENS
     */
    transitModeFilters?: TransitModeFilter[];

    /**
     * Maximum number of transfers allowed for transit itineraries. Defaults to server configuration when omitted.
     * Example: ITINERARY_TRANSIT_DEFAULT_MAX_TRANSFERS
     * Minimum: 0
     */
    maxTransfers?: number;

}
